const CLASS_TAG_PREFIX = 'class:';
const CLASS_NAME_TAG_PREFIX = 'class-name:';
const CREATOR_TAG_PREFIX = 'creator:';
const MOD_TAG_PREFIX = 'mod:';
const N_STORES_TAG_PREFIX = 'n_stores:';
const ORIENTATION_TAG_PREFIX = 'orientation:';
const STORES_TAG_PREFIX = 'stores:';
const TYPE_TAG_PREFIX = 'type:';
const SUBSET_TAG_PREFIX = 'subset:';
const MANAGED_CLASS_TAG_PREFIXES = [
  TYPE_TAG_PREFIX,
  ORIENTATION_TAG_PREFIX,
  N_STORES_TAG_PREFIX,
  STORES_TAG_PREFIX,
];

const LEGACY_TYPE_TAG_VALUES: Record<string, string> = {
  carton: 'carton',
  mixed: 'mixed',
  product: 'product',
  'template-box': 'carton',
  template_box: 'carton',
};

const LEGACY_ORIENTATION_TAG_VALUES: Record<string, string> = {
  front: 'front',
  mixed: 'mixed',
  nonfront: 'nonfront',
  'non-front': 'nonfront',
  'not-front': 'nonfront',
  notfront: 'nonfront',
};

const N_STORES_TAG_VALUES: Record<string, string> = {
  mixed: 'multiple',
  multiple: 'multiple',
  one: 'one',
  same: 'one',
};

export function normalizeTagPart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function isClassMetadataTag(tag: string): boolean {
  return tag.startsWith(CLASS_TAG_PREFIX) || tag.startsWith(CLASS_NAME_TAG_PREFIX);
}

function normalizeVizSimTagValues(value: string): string[] {
  const tag = value.trim();
  if (!tag || isClassMetadataTag(tag)) return [];

  if (tag.includes(':')) {
    const [rawPrefix, ...valueParts] = tag.split(':');
    const prefix = normalizeTagPart(rawPrefix);
    const rawValue = valueParts.join(':').trim();
    const normalizedValue = normalizeTagPart(rawValue);
    if (!prefix || !normalizedValue) return [];
    if (prefix === 'mod') return [`${MOD_TAG_PREFIX}${rawValue.toUpperCase()}`];
    if (prefix === 'type') {
      if (
        normalizedValue === 'front' ||
        normalizedValue === 'nonfront' ||
        normalizedValue === 'non-front' ||
        normalizedValue === 'notfront' ||
        normalizedValue === 'not-front'
      ) {
        return [
          `${TYPE_TAG_PREFIX}product`,
          `${ORIENTATION_TAG_PREFIX}${LEGACY_ORIENTATION_TAG_VALUES[normalizedValue]}`,
        ];
      }
      return [`${TYPE_TAG_PREFIX}${LEGACY_TYPE_TAG_VALUES[normalizedValue] ?? normalizedValue}`];
    }
    if (prefix === 'orientation')
      return [
        `${ORIENTATION_TAG_PREFIX}${LEGACY_ORIENTATION_TAG_VALUES[normalizedValue] ?? normalizedValue}`,
      ];
    if (['n-stores', 'n_stores', 'nstores', 'stores'].includes(prefix)) {
      return [`${N_STORES_TAG_PREFIX}${N_STORES_TAG_VALUES[normalizedValue] ?? normalizedValue}`];
    }
    if (prefix === 'subset') return [`${SUBSET_TAG_PREFIX}${normalizedValue}`];
    if (prefix === 'creator') return [`${CREATOR_TAG_PREFIX}${normalizedValue}`];
    return [`${prefix}:${normalizedValue}`];
  }

  const normalized = normalizeTagPart(tag);
  if (!normalized) return [];
  if (LEGACY_TYPE_TAG_VALUES[normalized]) {
    return [`${TYPE_TAG_PREFIX}${LEGACY_TYPE_TAG_VALUES[normalized]}`];
  }
  if (LEGACY_ORIENTATION_TAG_VALUES[normalized]) {
    return [`${ORIENTATION_TAG_PREFIX}${LEGACY_ORIENTATION_TAG_VALUES[normalized]}`];
  }
  return [`${SUBSET_TAG_PREFIX}${normalized}`];
}

export function normalizeVizSimTag(value: string): string | null {
  return normalizeVizSimTagValues(value)[0] ?? null;
}

export function normalizeVizSimTags(values: string[]): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  values.forEach((value) => {
    normalizeVizSimTagValues(value).forEach((tag) => {
      if (seen.has(tag)) return;
      seen.add(tag);
      tags.push(tag);
    });
  });
  return tags;
}

export function parseVizSimTags(value: string): string[] {
  return normalizeVizSimTags(value.split(','));
}

export function formatVizSimTags(tags: string[]): string {
  return tags.join(', ');
}

function tagValue(tags: string[], prefix: string): string | null {
  const tag = tags.find((value) => value.startsWith(prefix));
  return tag ? tag.slice(prefix.length) : null;
}

function mergeMixedValue(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  if (left === right) return left;
  return 'mixed';
}

function mergeStoreValue(
  leftTags: string[],
  rightTags: string[],
  leftStoreIds: number[],
  rightStoreIds: number[]
): string | null {
  const storeIds = new Set([...leftStoreIds, ...rightStoreIds]);
  if (storeIds.size > 1) return 'multiple';
  if (storeIds.size === 1) return 'one';
  return mergeMixedValue(
    tagValue(leftTags, N_STORES_TAG_PREFIX),
    tagValue(rightTags, N_STORES_TAG_PREFIX)
  );
}

export function mergeVizSimClassTagsForAppend(
  existingClassTags: string[],
  selectedCropTags: string[],
  existingClassStoreIds: number[] = [],
  selectedCropStoreIds: number[] = []
): string[] {
  const normalizedExistingTags = normalizeVizSimTags(existingClassTags);
  const normalizedSelectedTags = normalizeVizSimTags(selectedCropTags);
  const creatorValue =
    tagValue(normalizedSelectedTags, CREATOR_TAG_PREFIX) ??
    tagValue(normalizedExistingTags, CREATOR_TAG_PREFIX);
  const typeValue = mergeMixedValue(
    tagValue(normalizedExistingTags, TYPE_TAG_PREFIX),
    tagValue(normalizedSelectedTags, TYPE_TAG_PREFIX)
  );
  const orientationValue = mergeMixedValue(
    tagValue(normalizedExistingTags, ORIENTATION_TAG_PREFIX),
    tagValue(normalizedSelectedTags, ORIENTATION_TAG_PREFIX)
  );
  const storesValue = mergeStoreValue(
    normalizedExistingTags,
    normalizedSelectedTags,
    existingClassStoreIds,
    selectedCropStoreIds
  );
  const manualTags = [...normalizedExistingTags, ...normalizedSelectedTags].filter(
    (tag) =>
      !MANAGED_CLASS_TAG_PREFIXES.some((prefix) => tag.startsWith(prefix)) &&
      !tag.startsWith(CREATOR_TAG_PREFIX)
  );

  return normalizeVizSimTags([
    ...(typeValue ? [`${TYPE_TAG_PREFIX}${typeValue}`] : []),
    ...(orientationValue ? [`${ORIENTATION_TAG_PREFIX}${orientationValue}`] : []),
    ...(storesValue ? [`${N_STORES_TAG_PREFIX}${storesValue}`] : []),
    ...(creatorValue ? [`${CREATOR_TAG_PREFIX}${creatorValue}`] : []),
    ...manualTags,
  ]);
}

export function getVizSimTagValue(tags: string[], prefix: string): string | null {
  return tagValue(normalizeVizSimTags(tags), prefix);
}

export function getVizSimTagsByPrefix(tags: string[], prefix: string): string[] {
  return normalizeVizSimTags(tags)
    .filter((tag) => tag.startsWith(prefix))
    .map((tag) => tag.slice(prefix.length));
}

export function buildVizSimTags({
  type,
  orientation,
  nStores,
  creator,
  mods,
  subsets,
}: {
  type?: string | null;
  orientation?: string | null;
  nStores?: string | null;
  creator?: string | null;
  mods?: string[];
  subsets?: string[];
}): string[] {
  return normalizeVizSimTags([
    ...(type ? [`${TYPE_TAG_PREFIX}${type}`] : []),
    ...(orientation ? [`${ORIENTATION_TAG_PREFIX}${orientation}`] : []),
    ...(nStores ? [`${N_STORES_TAG_PREFIX}${nStores}`] : []),
    ...(creator ? [`${CREATOR_TAG_PREFIX}${creator}`] : []),
    ...(mods ?? []).map((value) => `${MOD_TAG_PREFIX}${value}`),
    ...(subsets ?? []).map((value) => `${SUBSET_TAG_PREFIX}${value}`),
  ]);
}

export function getVizSimCreatorTag(tags: string[]): string | null {
  return getVizSimTagValue(tags, CREATOR_TAG_PREFIX);
}

export function splitClassTags(tags: string[]): {
  classHash: string;
  className: string;
  regularTags: string[];
} {
  let classHash = '';
  let className = '';
  const regularTags: string[] = [];
  tags.forEach((tag) => {
    if (tag.startsWith(CLASS_TAG_PREFIX) && !classHash) {
      classHash = tag.slice(CLASS_TAG_PREFIX.length);
      return;
    }
    if (tag.startsWith(CLASS_NAME_TAG_PREFIX) && !className) {
      className = tag.slice(CLASS_NAME_TAG_PREFIX.length);
      return;
    }
    regularTags.push(tag);
  });
  return { classHash, className, regularTags: normalizeVizSimTags(regularTags) };
}
